import React, { useEffect, useState, useRef, useContext } from "react";
import {
  Box,
  Typography,
  Avatar,
  Card,
  CardContent,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Grid,
  Skeleton,
  Tabs,
  Tab,
  Tooltip,
} from "@mui/material";
import BuildIcon from "@mui/icons-material/Build";
import ReportProblemIcon from "@mui/icons-material/ReportProblem";
import SquareFootIcon from "@mui/icons-material/SquareFoot";
import Calendar from "../Calendar";
import api, { API_BASE_URL } from "../../utils/api";
import { useNavigate } from "react-router-dom";
import { ThemeContext } from "../../utils/ThemeContext";
import { ModulesContext, isModuleActive } from "../../utils/ModulesContext";

const CATEGORY_MAP = {
  workorders: {
    label: "Work Orders",
    shortLabel: "Work Orders",
    endpoint: "/top-members-workorders",
    key: "workOrderCount",
    icon: <BuildIcon sx={{ fontSize: 15 }} />,
    color: "#2f63ad",
    moduleId: 1,
  },
  violations: {
    label: "Violations",
    shortLabel: "Violations",
    endpoint: "/top-members-violations",
    key: "violationCount",
    icon: <ReportProblemIcon sx={{ fontSize: 15 }} />,
    color: "#d32f2f",
    moduleId: 2,
  },
  archrequests: {
    label: "Architectural Requests",
    shortLabel: "Arch. Requests",
    endpoint: "/top-members-arch-requests",
    key: "requestCount",
    icon: <SquareFootIcon sx={{ fontSize: 15 }} />,
    color: "#7b5ea7",
    moduleId: 3,
  },
};

const RANK_STYLES_LIGHT = [
  { bg: "#FFF8E1", color: "#F9A825", label: "#1" },
  { bg: "#F5F5F5", color: "#757575", label: "#2" },
  { bg: "#FBE9E7", color: "#BF360C", label: "#3" },
  { bg: "#f3f6fb", color: "#90a4ae", label: "#4" },
  { bg: "#f3f6fb", color: "#90a4ae", label: "#5" },
];

const RANK_STYLES_DARK = [
  { bg: "rgba(249,168,37,0.15)", color: "#F9A825", label: "#1" },
  { bg: "rgba(255,255,255,0.08)", color: "#b0bec5", label: "#2" },
  { bg: "rgba(191,54,12,0.18)", color: "#ff7043", label: "#3" },
  { bg: "rgba(144,164,174,0.12)", color: "#90a4ae", label: "#4" },
  { bg: "rgba(144,164,174,0.12)", color: "#90a4ae", label: "#5" },
];

const TopMembersWithCalendar = () => {
  const { isDark } = useContext(ThemeContext);
  const { modules } = useContext(ModulesContext);

  const activeCategories = Object.entries(CATEGORY_MAP).filter(
    ([, cfg]) => isModuleActive(modules, cfg.moduleId)
  );

  const defaultTab = activeCategories.length > 0 ? activeCategories[0][0] : "workorders";
  const [tab, setTab] = useState(defaultTab);
  const [data, setData] = useState({ workorders: [], violations: [], archrequests: [] });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const fetchedRef = useRef(new Set());
  const navigate = useNavigate();

  const fetchTopMembers = async (category) => {
    try {
      setLoading(true);
      setError(null);
      const res = await api.get(CATEGORY_MAP[category].endpoint);
      const top5 = res?.data?.members?.slice(0, 5) || [];
      setData((prev) => ({ ...prev, [category]: top5 }));
    } catch (err) {
      console.error(`Error fetching ${category}:`, err);
      setError(`Failed to load ${CATEGORY_MAP[category].label}.`);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!fetchedRef.current.has(tab)) {
      fetchedRef.current.add(tab);
      fetchTopMembers(tab);
    }
  }, [tab]);

  // If the current tab becomes inactive (module disabled), switch to first active one
  useEffect(() => {
    const isCurrentActive = activeCategories.some(([key]) => key === tab);
    if (!isCurrentActive && activeCategories.length > 0) {
      setTab(activeCategories[0][0]);
    }
  }, [modules]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleTabChange = (e, newValue) => setTab(newValue);

  const members = data[tab] ?? [];
  const statKey = CATEGORY_MAP[tab].key;
  const accentColor = CATEGORY_MAP[tab].color;
  const maxCount = members[0]?.[statKey] || 1;

  return (
    <Box sx={{ mt: 4, px: { xs: 0, md: 1 } }}>
      <Grid container spacing={3}>
        {/* Left Panel. Top Members */}
        <Grid item xs={12} md={4}>
          <Card
            sx={{
              borderRadius: "18px",
              boxShadow: "0 8px 24px rgba(0,0,0,0.10)",
              backgroundColor: isDark ? "#16223a" : "rgba(255,255,255,0.96)",
              backdropFilter: "blur(6px)",
              border: isDark ? "1px solid rgba(255,255,255,0.07)" : "1px solid rgba(47,99,173,0.08)",
              height: "100%",
            }}
          >
            <CardContent sx={{ p: 2.5 }}>
              <Typography
                variant="h6"
                sx={{ fontWeight: 700, color: isDark ? "#dde5f0" : "#223142", mb: 2, letterSpacing: "0.2px" }}
              >
                Top Members
              </Typography>

              <Tabs
                value={tab}
                onChange={handleTabChange}
                variant="scrollable"
                scrollButtons="auto"
                aria-label="Top Member Category Tabs"
                sx={{
                  mb: 2,
                  minHeight: "42px",
                  "& .MuiTabs-indicator": {
                    backgroundColor: accentColor,
                    height: 3,
                    borderRadius: "3px",
                  },
                  "& .MuiTab-root": {
                    minHeight: "42px",
                    textTransform: "none",
                    fontWeight: 600,
                    color: isDark ? "#8a9ab8" : "#6b778c",
                    px: 1.5,
                    gap: 0.6,
                  },
                  "& .Mui-selected": {
                    color: `${accentColor} !important`,
                  },
                }}
              >
                {activeCategories.map(([key, { shortLabel, icon }]) => (
                  <Tab
                    key={key}
                    value={key}
                    label={shortLabel}
                    icon={icon}
                    iconPosition="start"
                  />
                ))}
              </Tabs>

              {loading ? (
                <List sx={{ p: 0 }}>
                  {[...Array(5)].map((_, i) => (
                    <ListItem key={i} sx={{ px: 0, py: 1.2 }}>
                      <ListItemAvatar>
                        <Skeleton variant="circular" width={48} height={48} />
                      </ListItemAvatar>
                      <ListItemText
                        primary={<Skeleton width="60%" />}
                        secondary={
                          <>
                            <Skeleton width="45%" />
                            <Skeleton width="70%" sx={{ mt: 0.5 }} />
                          </>
                        }
                      />
                    </ListItem>
                  ))}
                </List>
              ) : error ? (
                <Typography color="error" align="center" sx={{ py: 3, fontWeight: 500 }}>
                  {error}
                </Typography>
              ) : members.length > 0 ? (
                <List sx={{ p: 0 }}>
                  {members.map((member, index) => {
                    const rankStyles = isDark ? RANK_STYLES_DARK : RANK_STYLES_LIGHT;
                    const rank = rankStyles[index] || rankStyles[4];
                    const count = member?.[statKey] || 0;
                    const pct = Math.round((count / maxCount) * 100);

                    return (
                      <Tooltip
                        key={member?.ClubMemberCode || index}
                        title="View profile"
                        placement="right"
                        arrow
                      >
                        <ListItem
                          onClick={() =>
                            navigate(`/home/clubmembers/${member?.ClubMemberCode}`)
                          }
                          sx={{
                            px: 0.5,
                            py: 1.2,
                            borderBottom:
                              index < members.length - 1
                                ? isDark ? "1px solid rgba(255,255,255,0.07)" : "1px solid #eef2f7"
                                : "none",
                            alignItems: "center",
                            cursor: "pointer",
                            borderRadius: "10px",
                            transition: "background 0.15s",
                            "&:hover": { backgroundColor: isDark ? "rgba(255,255,255,0.05)" : "rgba(47,99,173,0.04)" },
                          }}
                        >
                          {/* Rank badge */}
                          <Box
                            sx={{
                              width: 28,
                              height: 28,
                              borderRadius: "8px",
                              backgroundColor: rank.bg,
                              color: rank.color,
                              fontWeight: 800,
                              fontSize: "0.75rem",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              flexShrink: 0,
                              mr: 1,
                            }}
                          >
                            {rank.label}
                          </Box>

                          <ListItemAvatar sx={{ minWidth: 52 }}>
                            <Avatar
                              src={`${API_BASE_URL}/images/${encodeURIComponent(
                                member?.PhotoGraphicFileName?.trim() || ""
                              )}`}
                              alt="Member"
                              onError={(e) => (e.target.src = "/img/100x100.png")}
                              sx={{ width: 44, height: 44, border: isDark ? "2px solid rgba(255,255,255,0.1)" : "2px solid #f3f6fb" }}
                            />
                          </ListItemAvatar>

                          <ListItemText
                            primary={
                              <Typography
                                variant="body2"
                                sx={{ fontWeight: 700, color: isDark ? "#dde5f0" : "#223142", lineHeight: 1.3 }}
                              >
                                {member?.FirstName} {member?.LastName}
                              </Typography>
                            }
                            secondary={
                              <Box component="span" sx={{ display: "block", mt: 0.4 }}>
                                <Box
                                  sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "space-between",
                                    mb: 0.4,
                                  }}
                                >
                                  <Typography
                                    component="span"
                                    variant="caption"
                                    sx={{ color: isDark ? "#6b7fa0" : "#8a94a6" }}
                                  >
                                    {member?.ClubMemberCode || "N/A"}
                                  </Typography>
                                  <Typography
                                    component="span"
                                    variant="caption"
                                    sx={{
                                      fontWeight: 800,
                                      color: accentColor,
                                      fontSize: "0.82rem",
                                    }}
                                  >
                                    {count}
                                  </Typography>
                                </Box>

                                {/* Progress bar */}
                                <Box
                                  sx={{
                                    height: 5,
                                    borderRadius: "4px",
                                    backgroundColor: "rgba(0,0,0,0.06)",
                                    overflow: "hidden",
                                  }}
                                >
                                  <Box
                                    sx={{
                                      height: "100%",
                                      width: `${pct}%`,
                                      borderRadius: "4px",
                                      backgroundColor: accentColor,
                                      opacity: 0.75,
                                      transition: "width 0.4s ease",
                                    }}
                                  />
                                </Box>
                              </Box>
                            }
                          />
                        </ListItem>
                      </Tooltip>
                    );
                  })}
                </List>
              ) : (
                <Typography
                  variant="body2"
                  align="center"
                  sx={{ color: isDark ? "#8a9ab8" : "#7a8696", py: 3 }}
                >
                  No members found.
                </Typography>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Right Panel. Calendar */}
        <Grid item xs={12} md={8}>
          <Card
            sx={{
              borderRadius: "18px",
              boxShadow: "0 8px 24px rgba(0,0,0,0.10)",
              backgroundColor: isDark ? "#16223a" : "rgba(255,255,255,0.96)",
              backdropFilter: "blur(6px)",
              border: isDark ? "1px solid rgba(255,255,255,0.07)" : "1px solid rgba(47,99,173,0.08)",
              overflow: "hidden",
            }}
          >
            <Box sx={{ p: 2.5, pb: 1 }}>
              <Typography
                variant="h6"
                sx={{ fontWeight: 700, color: isDark ? "#dde5f0" : "#223142", letterSpacing: "0.2px" }}
              >
                Calendar
              </Typography>
            </Box>

            <Box
              sx={{
                px: 2.5,
                pb: 2.5,
                "& .fc": { fontFamily: "inherit" },
              }}
            >
              <Box
                sx={{
                  borderRadius: "14px",
                  backgroundColor: isDark ? "transparent" : "#fbfcfe",
                  border: isDark ? "none" : "1px solid #eef2f7",
                  p: isDark ? 0 : 1.5,
                  overflow: "hidden",
                }}
              >
                <Calendar defaultView="month" />
              </Box>
            </Box>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default TopMembersWithCalendar;
